import type { FC } from 'react';
import { useEffect } from 'react';
import { Provider, TextContext } from 'react-aria-components';
import type { SdkKey } from '@gonfalon/sdk-info/sdkInfo';
import { Dialog, Heading, IconButton, Modal, ModalOverlay } from '@launchpad-ui/components';

import { trackSdkSetupDrawerOpened } from '../../analytics';
import { useOnboardingStorage } from '../../hooks/useOnboardingStorage';
import { useInitializeOnboardingStorage } from '../../pages/SdkSetupPage/useInitializeOnboardingStorage';
import { SDK_SETUP_DESCRIPTION, SDK_SETUP_HEADING, SdkSetupTabs } from '../SdkSetupTabs';

import styles from './SdkSetupDrawer.module.css';

interface SdkSetupDrawerProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export const SdkSetupDrawer: FC<SdkSetupDrawerProps> = ({ isOpen, onOpenChange }) => (
  <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
    <Modal variant="drawer" className={styles.drawer}>
      <Dialog aria-label="Connect your app">{({ close }) => <SdkSetupDrawerContent close={close} />}</Dialog>
    </Modal>
  </ModalOverlay>
);

const SdkSetupDrawerContent: FC<{ close: () => void }> = ({ close }) => {
  const { sdk, setOnboardingStorage, resetOnboardingStorage } = useOnboardingStorage();

  useInitializeOnboardingStorage();

  useEffect(() => {
    trackSdkSetupDrawerOpened();
  }, []);

  useEffect(() => {
    return () => {
      resetOnboardingStorage();
    };
  }, [resetOnboardingStorage]);

  const handleSdkSelect = (selectedSdk: SdkKey) => {
    setOnboardingStorage({ sdk: selectedSdk });
  };

  return (
    <>
      <div slot="header">
        <Heading slot="title">{SDK_SETUP_HEADING}</Heading>
        <IconButton aria-label="Close" icon="cancel" size="small" variant="minimal" onPress={close} />
        <div slot="subtitle">{SDK_SETUP_DESCRIPTION}</div>
      </div>
      <div slot="body">
        <Provider values={[[TextContext, {}]]}>
          <div className={styles.body}>
            <SdkSetupTabs sdk={sdk} onSdkSelect={handleSdkSelect} />
          </div>
        </Provider>
      </div>
    </>
  );
};
